import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import { Link } from 'react-router-dom';
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import viictus from "@/assets/blog/viictus.jpg";
import cloud from "@/assets/blog/cloud.jpg";
import thermal1 from "@/assets/blog/thermal1.jpg";

// Blog post data
const blogPosts = [
  {
    id: 7,
    title: "Upgrading HP Victus fb0147AX: RAM from 8GB to 20GB",
    description: "Boosting performance on the Ryzen 5 5600H + RX 6500M combo with a simple RAM upgrade.",
    category: "Video",
    author: {
      name: "Mohan Balaji",
      avatar: "https://avatars.githubusercontent.com/u/134158954?v=4"
    },
    date: "June 2024",
    readTime: "2 min read",
    image: viictus,
    url: "/blog/Upgrading-hp-victus"
  },
  {
    id: 8,
    title: "Homelab Cloud Storage with TrueNAS & Cloudflare",
    description: "A deep dive into building a secure, self-hosted cloud storage solution using TrueNAS Scale and Cloudflare Tunnel. Learn about setup, security, remote access, and best practices for your own homelab.",
    category: "Tech",
    author: {
      name: "Mohan Balaji",
      avatar: "https://avatars.githubusercontent.com/u/134158954?v=4"
    },
    date: "June 2025",
    readTime: "8 min read",
    image:cloud,
    url: "/blog/homelab-cloud-truenas-cloudflare"
  },
  {
    id: 9,
    title: "Laptop Thermal Paste Replacement: Fix Overheating Issues",
    description: "Complete guide to replacing thermal paste on laptops to solve overheating problems. Learn when to replace, what thermal paste to use, and step-by-step instructions for safe disassembly.",
    category: "Tech",
    author: {
      name: "Mohan Balaji",
      avatar: "https://avatars.githubusercontent.com/u/134158954?v=4"
    },
    date: "January 2025",
    readTime: "6 min read",
    image: thermal1,
    url: "/blog/laptop-thermal-paste-replacement"
  }
];

const Blog = () => {
  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background w-full overflow-x-hidden">
        {/* Hero Section */}
        <section className="w-full px-4 sm:px-6 lg:px-8 pt-32 pb-24 sm:pt-40 sm:pb-32">
          <div className="w-full max-w-7xl mx-auto text-center">
            <Badge className="mb-4">Build Log</Badge>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-foreground">
              Behind the Work
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Engineering breakdowns, technical deep dives, and lessons learned.
            </p>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="w-full px-4 sm:px-6 lg:px-8 py-8">
          <div className="w-full max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {blogPosts.map((post) => (
                <Card
                  key={post.id}
                  className="bg-card group hover:-translate-y-0.5 transition-all duration-300 animate-card-enter border border-border shadow-sm overflow-hidden"
                 
                >
                  {/* Blog Post Image */}
                  <div className="relative overflow-hidden h-48 sm:h-56">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <img
                        src="https://avatars.githubusercontent.com/u/134158954?v=4"
                        alt="Blog Logo"
                        className="w-8 h-8 rounded-full border-2 border-primary shadow-md"
                      />
                      <Badge className="bg-primary/90 text-foreground">
                        {post.category}
                      </Badge>
                    </div>
                  </div>

                  <div className="p-6">
                    {/* Author Info */}
                    <div className="flex items-center gap-3 mb-4">
                      <img
                        src={post.author.avatar}
                        alt={post.author.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">{post.author.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{post.date}</span>
                          <span>---</span>
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Post Content */}
                    <h3 className="text-xl font-bold text-foreground mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                      <Link to={post.url}>{post.title}</Link>
                    </h3>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {post.description}
                    </p>

                    {/* Read More Link */}
                    <Link
                      to={post.url}
                      className="inline-flex items-center text-primary hover:underline text-sm group/link"
                    >
                      Read More
                      <ExternalLink className="ml-1 h-3 w-3 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="w-full px-4 sm:px-6 lg:px-8 py-8 border-t border-border">
          <div className="w-full max-w-7xl mx-auto text-center">
            <p className="text-sm text-muted-foreground">
              -- {new Date().getFullYear()} All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </>
  );
};

export default Blog; 
